import { makeAutoObservable, runInAction } from "mobx";
import { newUuid } from "../utils/uuid";
import { adminRoles, adminShareholderDetails, adminUsersSettings } from "../utils/navigation";
import { RadioFieldModel, StringFieldModel } from "../common/commonForms";
import { TMessage } from "../i18n/tt";
import { resolveUrnReference } from "../utils/UrnReference";
import { ExtendedShareholderDTO } from "./Shareholder";
import { DCEvent, HttpEvent, httpGet, httpPost } from "../events/eventsDomain";
import { PhoneNumber } from "../common/common";
import { DbotEventDispatcher } from "../dbot/common/commonDomain";

export type AddShareholdersFromPortfolioDialogParams = {
  excludeActors: string[];
  onSuccess: (shareholders: ExtendedShareholderDTO[]) => void;
  isMultiSelect?: boolean;
  excludeCompanies?: boolean;
  onClose?: () => void;
};

export enum ActorType {
  SHAREHOLDER = "SHAREHOLDER",
  BOARD_MEMBER = "BOARD_MEMBER",
  CEO = "CEO",
  ADMIN = "ADMIN",
  CUSTOM_ROLE = "CUSTOM_ROLE",
}

export type Actor = {
  type: ActorType;
  name: string;
  isCompany: boolean;
  companyNumber?: string;
  socialSecurityNumber?: string;
  birthDateOrOrganisationNumber?: string;
  nationality?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  availableActors: AvailableActor[];
};

export type AvailableActor = {
  reference: string;
  organisationNumber: string;
  companyName: string;
  representativeName?: string;
};

export type SelectedActor = {
  reference: string;
  name: string;
  isCompany: boolean;
  companyNumber?: string;
  socialSecurityNumber?: string;
  birthDateOrOrganisationNumber?: string;
  nationality?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  type: ActorType;
  organisationNumber: string;
};

export type ActorsSelectorModelParams = {
  title: TMessage;
  organisationNumber: string;
  tabs: ActorType[];
  excludeActors: string[];
  onLoad: (
    type: ActorType,
    onSuccess: (result: Actor[]) => DCEvent[],
    onFailure: () => DCEvent[],
  ) => void;
  onSubmit: (result: SelectedActor[], onError: () => void) => void;
  onClose: () => void;
  isMultiSelect?: boolean;
  excludeCompanies?: boolean;
  defaultCompanyFilterValue?: string | null;
};

export class ActorsSelectorModel {
  organisationNumber: string;
  actors: ActorModel[];
  tabs: ActorType[];
  selectedTab: ActorType;
  excludeActors: string[];
  excludeCompanies: boolean;

  title: TMessage;
  loadActors: (
    type: ActorType,
    onSuccess: (result: Actor[]) => DCEvent[],
    onFailure: () => DCEvent[],
  ) => void;
  onSubmit: (result: SelectedActor[], onError: () => void) => void;
  onClose: () => void;
  isMultiSelect: boolean;
  defaultCompanyFilterValue: string | null;

  searchFilter: StringFieldModel;
  companyFilter?: RadioFieldModel<string | null>;
  isLoading: boolean = false;

  constructor({
    title,
    organisationNumber,
    tabs,
    excludeActors,
    onLoad,
    onSubmit,
    onClose,
    isMultiSelect = true,
    excludeCompanies = false,
    defaultCompanyFilterValue,
  }: ActorsSelectorModelParams) {
    this.title = title;
    this.organisationNumber = organisationNumber;
    this.loadActors = onLoad;
    this.onSubmit = onSubmit;
    this.onClose = onClose;
    this.isMultiSelect = isMultiSelect;
    this.actors = [];

    this.tabs = tabs;
    this.selectedTab = tabs[0];
    this.excludeActors = excludeActors;
    this.excludeCompanies = excludeCompanies;
    this.defaultCompanyFilterValue =
      defaultCompanyFilterValue !== undefined ? defaultCompanyFilterValue : organisationNumber;

    this.searchFilter = new StringFieldModel();

    makeAutoObservable(this, {}, { autoBind: true });
  }

  fetchActors() {
    if (this.actors.some((a) => a.type === this.selectedTab)) {
      return;
    }
    this.setLoading(true);
    this.loadActors(
      this.selectedTab,
      (result) => {
        const filteredResults = result
          .filter((actor) => this.excludeBySSN(actor))
          .filter((actor) => this.excludeByCompanyNumber(actor))
          .filter((actor) => this.excludeById(actor))
          .filter((actor) => this.excludeByCompany(actor));

        this.addActors(filteredResults);
        this.initCompanyFilter();
        this.setLoading(false);
        return [];
      },
      () => {
        this.setLoading(false);
        return [];
      },
    );
  }

  addActors(actors: Actor[]) {
    this.actors.push(...actors.map((actor) => new ActorModel(actor)));
  }

  excludeBySSN(actor: Actor) {
    return !(
      actor.socialSecurityNumber !== undefined &&
      this.excludeActors.includes(actor.socialSecurityNumber)
    );
  }

  excludeByCompanyNumber(actor: Actor) {
    return !(actor.companyNumber !== undefined && this.excludeActors.includes(actor.companyNumber));
  }

  excludeById(actor: Actor) {
    return !actor.availableActors.some((a) => this.excludeActors.includes(a.reference));
  }

  excludeByCompany(actor: Actor) {
    return !(this.excludeCompanies && actor.isCompany);
  }

  get allCompanyOrgNos() {
    return [
      ...new Set(
        this.actorsByTab.flatMap((a) => a.availableActors.map((s) => s.organisationNumber)),
      ),
    ];
  }

  get allCompanies() {
    return [...new Set(this.actorsByTab.flatMap((a) => a.availableActors))];
  }

  findCompanyName(orgNo: string) {
    return this.allCompanies.find((a) => a.organisationNumber === orgNo)?.companyName ?? "";
  }

  initCompanyFilter() {
    const value =
      this.companyFilter !== undefined ? this.companyFilter.value : this.defaultCompanyFilterValue;
    this.companyFilter = new RadioFieldModel<string | null>({
      value: this.allCompanyOrgNos.find((orgNo) => orgNo === value) ?? null,
      options: [null, ...this.allCompanyOrgNos],
      idFn: (c) => (c ? c : "all"),
      labelFn: (c) =>
        c ? { translated: this.findCompanyName(c) } : { key: "portfolio.all-companies" },
    });
  }

  get actorsByTab() {
    return this.actors.filter((actor) => actor.type === this.selectedTab);
  }

  setSelectedTab(tab: ActorType) {
    this.selectedTab = tab;
    if (this.actors.every((actor) => actor.type !== tab)) {
      this.fetchActors();
    } else {
      this.initCompanyFilter();
    }
  }

  setLoading(value: boolean) {
    this.isLoading = value;
  }

  selectActor(actor: ActorModel) {
    if (!this.isMultiSelect) {
      this.setLoading(true);
      this.onSubmit([actor.toSelectedActor()], () => this.setLoading(false));
    }
  }

  get selectedActors() {
    return this.actors.filter((actor) => actor.isSelected);
  }

  submitActors() {
    this.setLoading(true);
    this.onSubmit(
      this.selectedActors.map((actor) => actor.toSelectedActor()),
      () => this.onClose(),
    );
  }
}

export class ActorModel {
  id: string;
  actor: Actor;
  type: ActorType;
  availableActors: AvailableActor[];
  selectedActor: AvailableActor;
  isSelected: boolean = false;

  constructor(actor: Actor) {
    this.actor = actor;
    this.id = newUuid();
    this.type = actor.type;
    this.availableActors = actor.availableActors;
    this.selectedActor = actor.availableActors[0];
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setSelectedActor(actor: AvailableActor) {
    this.selectedActor = actor;
  }

  toggleSelectedActor(value?: boolean) {
    this.isSelected = value ?? !this.isSelected;
  }

  get goToUrl() {
    switch (this.type) {
      case ActorType.SHAREHOLDER: {
        const reference = resolveUrnReference(this.selectedActor.reference);
        return reference !== undefined && reference.type === "shareholderId"
          ? adminShareholderDetails(this.selectedActor.organisationNumber, reference.id)
          : undefined;
      }
      case ActorType.BOARD_MEMBER:
      case ActorType.CEO:
        return adminRoles(this.selectedActor.organisationNumber);
      case ActorType.ADMIN:
      case ActorType.CUSTOM_ROLE:
        return adminUsersSettings(this.selectedActor.organisationNumber);
    }
  }

  matches(filterString: string, filterCompanyOrgNo?: string | null) {
    return (
      this.filterAvailableActors(filterCompanyOrgNo).length > 0 &&
      (this.actor.name.toLowerCase().includes(filterString) ||
        this.selectedActor.companyName.toLowerCase().includes(filterString) ||
        this.selectedActor.organisationNumber.includes(filterString) ||
        (this.selectedActor.representativeName !== undefined &&
          this.selectedActor.representativeName.toLowerCase().includes(filterString)) ||
        (this.actor.birthDateOrOrganisationNumber !== undefined &&
          this.actor.birthDateOrOrganisationNumber.includes(filterString)))
    );
  }

  filterAvailableActors(filterCompanyOrgNo?: string | null) {
    const availableActorsFiltered = filterCompanyOrgNo
      ? this.availableActors.filter((d) => d.organisationNumber === filterCompanyOrgNo)
      : this.availableActors;
    if (availableActorsFiltered.length === 1) {
      runInAction(() => {
        this.setSelectedActor(availableActorsFiltered[0]);
      });
    }
    return availableActorsFiltered;
  }

  toSelectedActor(): SelectedActor {
    return {
      reference: this.selectedActor.reference,
      name: this.actor.name,
      isCompany: this.actor.isCompany,
      companyNumber: this.actor.companyNumber,
      socialSecurityNumber: this.actor.socialSecurityNumber,
      birthDateOrOrganisationNumber: this.actor.birthDateOrOrganisationNumber,
      nationality: this.actor.nationality,
      email: this.actor.email,
      phoneNumber: this.actor.phoneNumber,
      type: this.type,
      organisationNumber: this.selectedActor.organisationNumber,
    };
  }
}

export function actorTypeLabel(t: ActorType): TMessage | undefined {
  switch (t) {
    case ActorType.SHAREHOLDER:
      return { key: "actors.type.shareholder" };
    case ActorType.BOARD_MEMBER:
      return { key: "actors.type.board-members" };
    case ActorType.CEO:
      return { key: "actors.type.ceos" };
    default:
      return undefined;
  }
}

export function loadActors(
  type: ActorType,
  onSuccess: (result: Actor[]) => DCEvent[],
  onFailure: () => DCEvent[],
  dispatch: DbotEventDispatcher,
  orgNo: string,
) {
  switch (type) {
    case ActorType.SHAREHOLDER:
      dispatch(actorsApi.getShareholders({ orgNo, onSuccess, onFailure }));
      break;
    case ActorType.BOARD_MEMBER:
    case ActorType.CEO:
      dispatch(actorsApi.getCompanyRoles({ orgNo, type, onSuccess, onFailure }));
      break;
    default:
      onSuccess([]);
  }
}

export const actorsApi = {
  getShareholders: ({ orgNo, onSuccess, onFailure }: GetShareholderAsActorsRequest): HttpEvent =>
    httpGet({
      url: `/shareholder/${orgNo}/actors`,
      onSuccess: (res) => onSuccess(res.data),
      onFailure,
    }),

  getCompanyRoles: ({
    orgNo,
    type,
    onSuccess,
    onFailure,
  }: GetCompanyRolesAsActorsRequest): HttpEvent =>
    httpGet({
      url: `/company-roles/${orgNo}/actors/${type}`,
      onSuccess: (res) => onSuccess(res.data),
      onFailure,
    }),

  addShareholdersFromActors: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: AddShareholdersFromActorsRequest): HttpEvent =>
    httpPost({
      url: `/shareholder/${orgNo}/actors`,
      data,
      onSuccess: (res) => onSuccess(res.data),
      onFailure,
    }),
};

type GetShareholderAsActorsRequest = {
  orgNo: string;
  onSuccess: (res: Actor[]) => DCEvent[];
  onFailure: () => DCEvent[];
};

type GetCompanyRolesAsActorsRequest = {
  orgNo: string;
  type: ActorType;
  onSuccess: (res: Actor[]) => DCEvent[];
  onFailure: () => DCEvent[];
};

type AddShareholdersFromActorsRequest = {
  orgNo: string;
  data: SelectedActor[];
  onSuccess: (res: ExtendedShareholderDTO[]) => DCEvent[];
  onFailure: () => DCEvent[];
};
